<template>
  <div class="space-y-2">
    <span class="mb-2 block font-medium">
      {{ $t('area.analysis-test.answer.title') }}
    </span>
    <span
      v-if="description"
      class="-mt-2 mb-2 block text-sm text-text-primary">
      {{ description }}
    </span>

    <UiEditableList
      v-if="activeAnswers.length"
      :items="activeAnswers"
      itemKey="id"
      compact
      :isDraggable="canUser('setup.manage')"
      :isRemovable="false"
      @update:items="onReorderActive">
      <template #item="{ item }">
        <div class="flex flex-1 items-center gap-2 [&_.s-vee-form-field]:mb-0">
          <div class="flex-1">
            <UiInputTextFieldVee
              :name="`answer-${item.id}`"
              :unit="currentLocale.toUpperCase()"
              :rules="['required']"
              isRequired
              v-model="item.text[currentLocale]" />
          </div>
          <IconLucideTriangleAlert
            v-if="hasTextChanged(item)"
            v-tooltip="$t('W: Opdatering af denne tekst vil opdatere alle rapporter, der i øjeblikket bruger den — overvej i stedet at deaktivere denne mulighed og tilføje en ny.')"
            class="h-4 w-4 shrink-0 text-feedback-warning" />
          <!-- Fixed width so the row's right edge lands in the same place whether
               this slot renders the remove ✕ (new answers) or the switch (saved
               ones) — those two controls aren't the same width. -->
          <div class="flex w-11 shrink-0 items-center justify-end">
            <button
              v-if="item._isNew && canUser('setup.manage')"
              type="button"
              class="cursor-pointer p-2 text-text-secondary hover:text-text-primary"
              @click="removeAnswer(item)">
              <IconLucideX class="h-5 w-5" />
            </button>
            <AppSwitch
              v-else-if="!item._isNew && canUser('setup.manage')"
              :isActive="true"
              :title="$t('action.deactivate')"
              @on-toggle="toggleAnswerState(item)" />
          </div>
        </div>
      </template>
    </UiEditableList>

    <template v-if="inactiveAnswers.length">
      <span class="mt-4 mb-1 block text-sm font-medium text-text-secondary">
        {{ $t('W: Deaktiveret') }}
      </span>
      <!-- No drag-and-drop here — order is always alphabetical, and dragging
           a locked row around a list it can never leave doesn't mean anything. -->
      <UiEditableList
        :items="inactiveAnswers"
        itemKey="id"
        compact
        :isRemovable="false">
        <template #item="{ item }">
          <div class="flex flex-1 items-center gap-2 opacity-50 [&_.s-vee-form-field]:mb-0">
            <div class="flex-1">
              <UiInputTextFieldVee
                :name="`answer-${item.id}`"
                :unit="currentLocale.toUpperCase()"
                isDisabled
                v-model="item.text[currentLocale]" />
            </div>
            <div class="flex w-11 shrink-0 items-center justify-end">
              <AppSwitch
                v-if="canUser('setup.manage')"
                :isActive="false"
                :title="$t('action.activate')"
                @on-toggle="toggleAnswerState(item)" />
            </div>
          </div>
        </template>
      </UiEditableList>
    </template>

    <UiBaseButton
      v-if="canUser('setup.manage')"
      :text="$t('area.analysis-test.answer.add')"
      @button-click="addAnswer" />
  </div>
</template>

<script lang="ts">
import { useAppStore } from '@/stores/appStore'
import { useUserStore } from '@/stores/userStore'
import { mapActions, mapState } from 'pinia'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import type { PropType } from 'vue'
import type { IAnalysisTestAnswer } from '@team-milva/lab-models/resources'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { ITextObj } from '@team-milva/lab-models/textObj'

type TAnswerForm = IAnalysisTestAnswer & { _isNew?: boolean }

export default {
  name: 'AnalysisTestAnswersSection',

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Array as PropType<TAnswerForm[]>,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    // Bumped by the parent only in its onSave() success path (e.g. a timestamp) — refreshes the
    // rename-warning baseline below to the just-saved text. Not tied to modelValue itself, since
    // that changes on every keystroke too, well before any save happens.
    resetSignal: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      localAnswers: [...this.modelValue] as TAnswerForm[],
      // Snapshotted once, at mount, from the saved values this pane opened with —
      // never re-synced from a later modelValue change, since every local edit here
      // round-trips straight back through the parent's v-model into that same prop.
      // Used only to detect a rename on an already-saved answer (Phase 6 of the
      // Analysis Setup Stability plan) — re-opening the pane starts a fresh snapshot.
      originalAnswerTexts: Object.fromEntries(
        this.modelValue.map((answer) => [answer.id, { ...answer.text }])
      ) as Record<string, ITextObj>,
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useUserStore, ['currentUser']),

    // Its own draggable UiEditableList — manual order, drag-and-drop enabled.
    activeAnswers(): TAnswerForm[] {
      return this.localAnswers.filter((a) => a.activeState === 'active')
    },

    // A separate, non-draggable list below — always sorted by name. Splitting
    // this out (rather than one combined list with inactive rows sorted to
    // the bottom) is what it took to keep drag-and-drop coherent: dragging
    // within a list that silently re-sorts part of itself out from under the
    // pointer doesn't work as an interaction.
    inactiveAnswers(): TAnswerForm[] {
      return [...this.localAnswers.filter((a) => a.activeState !== 'active')].sort(
        (a, b) =>
          getTextObjValue(a.text, this.currentLocale).localeCompare(
            getTextObjValue(b.text, this.currentLocale)
          )
      )
    },
  },

  watch: {
    modelValue(newVal: TAnswerForm[]) {
      this.localAnswers = [...newVal]
    },
    // The parent only bumps this in its onSave() success path — refresh the rename-warning
    // baseline to what was actually just saved, same as AnalysisTestEditDetailsView.vue does
    // for its own originalName/originalDisplayName.
    resetSignal() {
      this.originalAnswerTexts = Object.fromEntries(
        this.localAnswers.map((answer) => [answer.id, { ...answer.text }])
      )
    },
  },

  methods: {
    ...mapActions(useUserStore, ['canUser']),

    addAnswer() {
      if (!this.canUser('setup.manage')) return
      this.localAnswers = [
        ...this.localAnswers,
        {
          id: generateFirestoreId(),
          createdByUserID: this.currentUser?.id ?? '',
          text: { [this.currentLocale]: '' },
          activeState: 'active' as const,
          createdAt: Date.now(),
          _isNew: true,
        },
      ]
      this.$emit('update:modelValue', this.localAnswers)
    },

    removeAnswer(answer: TAnswerForm) {
      if (!this.canUser('setup.manage')) return
      this.localAnswers = this.localAnswers.filter((a) => a.id !== answer.id)
      this.$emit('update:modelValue', this.localAnswers)
    },

    toggleAnswerState(answer: TAnswerForm) {
      if (!this.canUser('setup.manage')) return
      answer.activeState =
        answer.activeState === 'active' ? 'inactive' : 'active'
      this.$emit('update:modelValue', [...this.localAnswers])
    },

    // The active list is its own UiEditableList, so `reordered` is already
    // just the active subset in its new order — no filtering needed.
    onReorderActive(reordered: TAnswerForm[]) {
      if (!this.canUser('setup.manage')) return
      const inactive = this.localAnswers.filter((a) => a.activeState !== 'active')
      this.localAnswers = [...reordered, ...inactive]
      this.$emit('update:modelValue', this.localAnswers)
    },

    // Advisory only — see the warning icon in the template. Never blocks saving.
    // Scoped to the current locale, since that's the only slot this row's input
    // edits — comparing the whole ITextObj would also be brittle to
    // insertion-order differences between the two objects.
    hasTextChanged(answer: TAnswerForm): boolean {
      if (answer._isNew) return false
      const original = this.originalAnswerTexts[answer.id]
      if (!original) return false
      return answer.text[this.currentLocale] !== original[this.currentLocale]
    },
  },
}
</script>

<style scoped></style>

